import { SupportedLanguage } from '@/shared/legacy/contexts/LocalizationContext/types';
import { AxiosPromise } from 'axios';
import { CampaignJobboard } from 'shared/legacy/contexts/SelectedCampaignContext/SelectedCampaignContext.types';

/////////////////////////////
/// DTOs
/////////////////////////////

export enum TimeUnit {
  HOUR = 'hour',
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
}

export type QuestionUnit = TimeUnit;

export enum QuestionKey {
  READY_FOR_JOB_OFFERS = 'ready_for_job_offers',
  CERTIFICATES_AND_LICENSES = 'certificates_and_licenses',
  SALARY = 'salary',
  SHIFTS = 'shifts',
  AVAILABLE_DAYS = 'available_days',
  LIMITATIONS_AND_PREFERENCES = 'limitations_and_preferences',
  WORKING_HOURS_PER_WEEK = 'working_hours_per_week',
  LANGUAGES = 'languages',
}

export type RangeValue = {
  min: number;
  max: number;
};

export enum InputType {
  RANGE_SLIDER = 'range_slider',
  RANGE_INPUT = 'range_input',
  STRING = 'string',
  ARRAY = 'array',
  BOOLEAN = 'boolean',
}

export type RequirementField<T> = {
  question_key: QuestionKey;
  job_requirements?: T;
  confirmed_info?: T;
  is_answered: boolean;
  input_type?: InputType;
  unit?: QuestionUnit;
  is_required?: boolean;
};

export type CandidateRequirementsDTO = {
  [QuestionKey.READY_FOR_JOB_OFFERS]: RequirementField<boolean>;
  [QuestionKey.CERTIFICATES_AND_LICENSES]: RequirementField<string[]>;
  [QuestionKey.SALARY]: RequirementField<RangeValue>;
  [QuestionKey.SHIFTS]: RequirementField<string[]>;
  [QuestionKey.AVAILABLE_DAYS]: RequirementField<string[]>;
  [QuestionKey.LIMITATIONS_AND_PREFERENCES]: RequirementField<string[]>;
  [QuestionKey.WORKING_HOURS_PER_WEEK]: RequirementField<RangeValue>;
  [QuestionKey.LANGUAGES]: RequirementField<string[]>;
};

export type TenantBrandingDTO = {
  logo_url: string | null;
  primary_color: string | null;
};

export type CandidateQuestionnaireDTO = {
  candidate: {
    id: string;
    name: string;
    source: CampaignJobboard;
    communication_opt_out?: boolean;
    last_insight_date?: string;
  };
  campaign: {
    user_id: number;
    user_name: string;
    tenant_id: number;
    tenant_name: string;
  };
  candidate_requirements: CandidateRequirementsDTO;
  selected_language?: 'en' | 'nl';
  branding?: TenantBrandingDTO;
};

export type SubmitQuestionnaireResponseDTO = {
  can_schedule: boolean;
  scheduler_link?: string | null;
  sent_via?: 'robin_jr' | null;
};

/////////////////////////////
/// Handlers
/////////////////////////////

export type GetCandidateQuestionnaireHandler = (
  params: GetCandidateQuestionnaireParams,
) => AxiosPromise<CandidateQuestionnaireDTO>;

export type GetCandidateQuestionnaireParams = {
  token: string;
};

export type SubmitCandidateQuestionnaireHandler = (
  params: SubmitCandidateQuestionnaireParams,
) => AxiosPromise<SubmitQuestionnaireResponseDTO>;

export type SubmitCandidateQuestionnaireParams = {
  token: string;
  candidateQuestionnaire: CandidateQuestionnaireDTO;
};

export type GetQuestionnairePreviewHandler = (
  params: GetQuestionnairePreviewParams,
) => AxiosPromise<CandidateRequirementsDTO>;

export type GetQuestionnairePreviewParams = {
  es_person_id: string;
  job_id: number;
  selected_language: SupportedLanguage;
};

/////////////////////////////
/// Candidate Profile DTOs
/////////////////////////////

export type CandidateProfileDTO = {
  candidate: {
    id: string;
    name: string;
    source: CampaignJobboard;
    communication_opt_out?: boolean;
    last_insight_date?: string;
  };
  campaign: null;
  job_requirements: null;
  candidate_requirements: CandidateRequirementsDTO;
  selected_language?: 'en' | 'nl';
};

export type GetCandidateProfileHandler = (params: GetCandidateProfileParams) => AxiosPromise<CandidateProfileDTO>;

export type GetCandidateProfileParams = {
  token: string;
};

export type UpdateCandidateProfileHandler = (params: UpdateCandidateProfileParams) => AxiosPromise<CandidateProfileDTO>;

export type UpdateCandidateProfileParams = {
  token: string;
  candidateProfile: CandidateProfileDTO;
};

/////////////////////////////
/// Questionnaire Configuration (by job)
/////////////////////////////

export type QuestionRequirementConfigJobRequirements = boolean | string[] | Partial<RangeValue>;

export type QuestionRequirementConfig = Required<
  Pick<RequirementField<QuestionRequirementConfigJobRequirements>, 'job_requirements' | 'is_required'>
>;

export type QuestionnaireRequirements = Partial<Record<QuestionKey, QuestionRequirementConfig>>;

export type QuestionnaireConfigurationResponse = {
  id: number;
  job_improved_id: number;
  questionnaire_requirements: QuestionnaireRequirements;
  pending_review?: boolean;
};

export type QuestionnaireConfigurationCreateRequest = {
  job_id: number;
};

export type QuestionnaireConfigurationUpdateRequest = {
  job_id: number;
  questionnaire_requirements: QuestionnaireRequirements;
  pending_review?: boolean;
};

export type UpdateQuestionnaireConfigurationHandler = (
  data: QuestionnaireConfigurationUpdateRequest,
) => AxiosPromise<QuestionnaireConfigurationResponse>;

export type EnsureQuestionnaireConfigurationHandler = (
  data: QuestionnaireConfigurationCreateRequest,
) => AxiosPromise<QuestionnaireConfigurationResponse>;
